"use client";

/**
 * NeonMascot — animação premium sobre o PNG original (intocado).
 *
 * React + Framer Motion  →  funciona em React puro e em Next.js (App/Pages Router).
 *
 * Instalação:   npm i framer-motion
 * Uso (Next):   coloque boriscdn.png em /public e renderize:
 *
 *   <NeonMascot src="/boriscdn.png" size={400} />
 *
 * Efeitos: flutuação idle, respiração 100→102%, micro-rotação ±1°,
 * pulso de brilho neon, piscar a cada 4–8 s, reflexo nas lentes,
 * partículas discretas. 100% transform/opacity (GPU, 60 fps).
 * Geometria das lentes/olhos calibrada pixel a pixel no PNG 1024×1024.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";

/* ---------- geometria medida no PNG original (% da imagem) ---------- */
const LENSES = {
  left: {
    box: { left: "28.4%", top: "45.2%", width: "18.8%", height: "11.4%" },
    lid: "linear-gradient(180deg,#4a0a78 0%,#9a1cc0 45%,#d419e4 100%)",
  },
  right: {
    box: { left: "53.3%", top: "44.4%", width: "18.4%", height: "11.2%" },
    lid: "linear-gradient(180deg,#0b3fb0 0%,#1273f2 45%,#27a4fd 100%)",
  },
};

const PARTICLE_COLORS = ["#ff4fd8", "#7a5cff", "#3da9ff"];
const rand = (a, b) => a + Math.random() * (b - a);

/* ---------- sub-componentes ---------- */

function Lens({ cfg, blinking, glint, delay, reduced }) {
  return (
    <div
      style={{
        position: "absolute",
        ...cfg.box,
        overflow: "hidden",
        borderRadius: "26% 26% 30% 30% / 44% 44% 52% 52%",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      {/* pálpebra — cor amostrada do pelo dentro da lente */}
      <motion.div
        initial={false}
        animate={{ scaleY: blinking ? 1 : 0 }}
        transition={{
          duration: blinking ? 0.09 : 0.14,
          ease: blinking ? [0.3, 0, 0.7, 0] : [0.2, 0, 0.4, 1],
        }}
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: "top center",
          background: cfg.lid,
          borderRadius: "0 0 46% 46% / 0 0 64% 64%",
          boxShadow:
            "inset 0 -6px 10px -4px rgba(0,0,0,.45), inset 0 4px 6px -3px rgba(255,255,255,.22)",
          willChange: "transform",
        }}
      />
      {/* reflexo varrendo a lente */}
      {!reduced && (
        <motion.div
          animate={glint}
          initial={{ x: "-240%" }}
          custom={delay}
          style={{
            position: "absolute",
            top: "-30%",
            height: "160%",
            width: "36%",
            left: 0,
            background:
              "linear-gradient(105deg,transparent 18%,rgba(255,255,255,.55) 50%,transparent 82%)",
            mixBlendMode: "screen",
            opacity: 0.75,
            filter: "blur(1px)",
          }}
        />
      )}
    </div>
  );
}

function Particles({ reduced }) {
  const [items, setItems] = useState([]);

  /* gerado no cliente (evita mismatch de hidratação no Next) */
  useEffect(() => {
    setItems(
      Array.from({ length: 12 }, (_, i) => {
        const ang = rand(0, Math.PI * 2);
        const radius = rand(38, 57); // % a partir do centro
        return {
          id: i,
          x: 50 + Math.cos(ang) * radius,
          y: 50 + Math.sin(ang) * radius * 0.92,
          s: rand(2.5, 5.5),
          c: PARTICLE_COLORS[i % 3],
          o: rand(0.35, 0.7),
          d: rand(6, 12),
          del: rand(0, 9),
          dx: rand(-14, 14),
        };
      })
    );
  }, []);

  if (reduced) return null;

  return (
    <div style={{ position: "absolute", inset: "-8%", zIndex: 0, pointerEvents: "none" }}>
      {items.map((p) => (
        <motion.span
          key={p.id}
          animate={{ y: [10, -52], x: [0, p.dx], opacity: [0, p.o, p.o, 0] }}
          transition={{
            duration: p.d,
            delay: p.del,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.18, 0.82, 1],
          }}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.s,
            height: p.s,
            borderRadius: "50%",
            background: p.c,
            boxShadow: `0 0 ${p.s * 2.4}px ${p.c}`,
            willChange: "transform, opacity",
          }}
        />
      ))}
    </div>
  );
}

/* ---------- componente principal ---------- */

export default function NeonMascot({ src = "/boriscdn.png", size = 400, className }) {
  const reduced = useReducedMotion();
  const [blinking, setBlinking] = useState(false);
  const glint = useAnimationControls();
  const timers = useRef([]);

  /* piscar natural a cada 4–8 s (com piscada dupla ocasional) */
  useEffect(() => {
    if (reduced) return;
    let alive = true;
    const blinkOnce = () => {
      setBlinking(true);
      timers.current.push(setTimeout(() => setBlinking(false), 110));
    };
    const loop = () => {
      timers.current.push(
        setTimeout(() => {
          if (!alive) return;
          blinkOnce();
          if (Math.random() < 0.18) timers.current.push(setTimeout(blinkOnce, 270));
          loop();
        }, rand(4000, 8000))
      );
    };
    loop();
    return () => {
      alive = false;
      timers.current.forEach(clearTimeout);
    };
  }, [reduced]);

  /* reflexo ocasional nas lentes */
  useEffect(() => {
    if (reduced) return;
    let alive = true;
    const loop = () => {
      timers.current.push(
        setTimeout(async () => {
          if (!alive) return;
          await glint.start((delay) => ({
            x: ["-240%", "380%"],
            transition: { duration: 0.95, delay, ease: "easeInOut" },
          }));
          glint.set({ x: "-240%" });
          loop();
        }, rand(5500, 9500))
      );
    };
    loop();
    return () => {
      alive = false;
    };
  }, [reduced, glint]);

  const idle = (anim, duration) =>
    reduced ? {} : { animate: anim, transition: { duration, repeat: Infinity, ease: "easeInOut" } };

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: `min(${size}px, 84vw)`, /* desktop 300/400/500 — mobile adapta */
        aspectRatio: "1 / 1",
      }}
    >
      <Particles reduced={reduced} />

      {/* camadas aninhadas: flutuação → rotação → respiração */}
      <motion.div style={{ position: "absolute", inset: 0, willChange: "transform" }} {...idle({ y: ["0%", "-2.2%", "0%"] }, 4.6)}>
        <motion.div style={{ position: "absolute", inset: 0, willChange: "transform" }} {...idle({ rotate: [-1, 0, 1, 0, -1] }, 8.4)}>
          <motion.div style={{ position: "absolute", inset: 0, willChange: "transform" }} {...idle({ scale: [1, 1.02, 1] }, 5.6)}>

            {/* brilho neon pulsante: cópia desfocada ATRÁS da arte original */}
            <motion.img
              src={src}
              alt=""
              aria-hidden
              draggable={false}
              {...idle({ opacity: [0.55, 0.85, 0.55] }, 3.8)}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "blur(26px) saturate(1.35)",
                transform: "scale(1.06) translateZ(0)",
                opacity: 0.55,
                WebkitMaskImage:
                  "radial-gradient(ellipse 52% 50% at 50% 48%, #000 52%, transparent 76%)",
                maskImage:
                  "radial-gradient(ellipse 52% 50% at 50% 48%, #000 52%, transparent 76%)",
                willChange: "opacity",
              }}
            />

            {/* a arte original — exatamente o PNG fornecido, sem nenhuma alteração */}
            <img
              src={src}
              alt="Boris, mascote bulldog francês neon de óculos"
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                zIndex: 1,
              }}
            />

            <Lens cfg={LENSES.left}  blinking={blinking} glint={glint} delay={0}    reduced={reduced} />
            <Lens cfg={LENSES.right} blinking={blinking} glint={glint} delay={0.12} reduced={reduced} />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Exemplo de página (Next.js App Router) — app/page.jsx:

   import NeonMascot from "@/components/NeonMascot";

   export default function Page() {
     return (
       <main style={{ minHeight: "100svh", display: "grid",
                      placeItems: "center", background: "#000" }}>
         <NeonMascot src="/boriscdn.png" size={400} />
       </main>
     );
   }

   Obs.: o PNG tem fundo preto sólido — use-o sobre fundo preto (#000)
   para integração perfeita, como no exemplo acima.
------------------------------------------------------------------- */
